/**
 * Drop Sentry events thrown by scripts that social in-app browsers inject
 * into our page.
 *
 * Instagram's iOS webview injects a login-field sniffer that calls its native
 * bridge via postMessage; when that bridge rejects the call it throws
 * `InvalidAccessError: The object does not support the operation or argument`
 * inside Instagram's own wrapper. It runs in our page's context with no source
 * file, so Sentry attributes it to us (`app:///checkout`, inApp=true) and pages
 * on it — six times during one live door.
 *
 * `ignoreErrors` cannot catch it: that matches the MESSAGE, and the message
 * here is a generic DOM error we must not blanket-suppress. What identifies
 * the injected script is the STACK — function names that exist nowhere in
 * this codebase. So this matches on frames, and only on frames.
 *
 * Kept deliberately narrow: only names observed in real captured stacks.
 * Adding speculative patterns is how real errors get hidden.
 */

/** Function names seen in injected in-app-browser bridge stacks. */
export const INJECTED_BRIDGE_FRAMES = /^(dispatchToBridge|logLoginFieldDetected|sendPostMessage)$/

/** Minimal structural view of a Sentry event; compatible with `Event` from
 *  @sentry/types without importing it. */
export interface FrameBearingEvent {
  exception?: {
    values?: Array<{
      stacktrace?: { frames?: Array<{ function?: string | null }> } | null
    }> | null
  } | null
}

export function isInjectedBridgeError(event: FrameBearingEvent | null | undefined): boolean {
  const values = event?.exception?.values
  if (!values) return false
  for (const v of values) {
    const frames = v?.stacktrace?.frames
    if (!frames) continue
    for (const f of frames) {
      if (f?.function && INJECTED_BRIDGE_FRAMES.test(f.function)) return true
    }
  }
  return false
}
